import { AnimatePresence, motion } from 'framer-motion'

import { useGetCompany } from '@apps-common/apis/portal/company/get-company'
import { CompanyDetailsUpdateModal } from '@apps-common/apps/portal/pages/settings/components'
import { CardContainer } from '@apps-common/components/common/CardContainer'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { CompanyProfileImagePlaceholder } from '@apps-common/components/common/user/CompanyProfileImagePlaceholder'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

const CompanyDetailsSummaryButtonSkeleton = () => (
  <>
    <CardContainer bgClasses='!bg-skeleton border border-skeleton-border px-3 pt-2 pb-4 sm:px-5 sm:py-4'>
      <div className='flex items-start justify-between'>
        <div className='bg-skeleton-hi w-[100px] h-[27px] rounded-lg' />
        <div className='bg-skeleton-hi w-[30px] h-[30px] rounded-[15px]' />
      </div>
      <VertSpacerMd />
      <div className='bg-skeleton text-text rounded-[30px] p-2 py-3 sm:p-3 sm:py-4 w-full'>
        <div className='flex flex-col items-center justify-start gap-4 sm:gap-[15px] max-w-full px-4 sm:px-5 sm:py-4'>
          <div className='h-[72px] bg-skeleton-hi rounded-2xl w-full' />
          <div className='text-left w-full px-2 flex flex-col gap-2'>
            <div className='bg-skeleton-hi rounded-lg w-[180px] h-[26px]' />
            <div className='bg-skeleton-hi rounded-lg w-[235px] h-[23px]' />
            <div className='bg-skeleton-hi rounded-lg w-[180px] h-[21px]' />
          </div>
        </div>
      </div>
    </CardContainer>
  </>
)

export const CompanyDetailsSummaryButton = () => {
  const { data, isLoading, isError } = useGetCompany({})

  const loading = isLoading || isError

  return (
    <AnimatePresence mode='popLayout'>
      {loading && (
        <motion.div key='settings-company-details-summary-button-skeleton' {...motionFadeIn(0.2)}>
          <CompanyDetailsSummaryButtonSkeleton />
        </motion.div>
      )}
      {!loading && (
        <motion.div key='settings-company-details-summary-button' {...motionFadeIn(0.2)}>
          <ModalButton
            component={CompanyDetailsUpdateModal}
            disabled={false}
            componentProps={{}}
            classes='w-full'
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <CardContainer bgClasses='!bg-surface border border-border px-3 pt-2 pb-4  sm:px-5 sm:py-4'>
              <div className='flex items-center justify-between'>
                <SectionTitle classes='text-text-muted/70 text-left pl-[10px] pr-[5px]' text='Company' />
                <ButtonRoundSm type='button' disabled={isLoading} className='action-brand-solid' callback={() => null}>
                  <commonIcons.arrowAngled />
                </ButtonRoundSm>
              </div>
              <VertSpacerMd />
              <div className='bg-surface-2 text-text rounded-[30px] p-2 py-3 sm:p-3 sm:py-4 w-full whitespace-nowrap overflow-hidden'>
                <div className='jost-light flex flex-col items-center justify-start gap-4 sm:gap-[15px] max-w-full whitespace-nowrap overflow-hidden px-4 sm:px-5 sm:py-4'>
                  <div className='bg-surface border border-border rounded-2xl flex items-center justify-center overflow-hidden w-full'>
                    <div className='bg-logo-background w-full h-full flex items-center justify-center p-[10px]'>
                      <CompanyProfileImagePlaceholder classes='ml-[2px] overflow-hidden' />
                    </div>
                  </div>
                  <div className='text-left jost-light w-full overflow-hidden px-2'>
                    <div className='jost text-[1.1em] sm:text-[1.2em] overflow-hidden text-ellipsis'>{data?.displayName}</div>
                    <div className='text-[0.9em] sm:text-[1em] overflow-hidden text-ellipsis'>{data?.email}</div>
                    <div className='text-[0.9em] sm:text-[0.95em] overflow-hidden text-ellipsis'>{data?.phone}</div>
                  </div>
                </div>
              </div>
            </CardContainer>
          </ModalButton>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
